
import { useState, useEffect } from "react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import TimeSelector from "./TimeSelector";
import DurationSelector from "./DurationSelector";
import VehicleNumberInput from "./VehicleNumberInput";
import FeedbackForm from "@/components/feedback/FeedbackForm";
import { AuthPromptDialog } from "@/components/auth/AuthPromptDialog";
import { useBooking } from "@/hooks/useBooking";
import { CalendarCheck, Clock, Car, CreditCard, Warehouse } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface BookingDialogProps {
  children: React.ReactNode;
  onBook: (date: Date, time: string, hours: string, vehicleNumber: string, lotCount: number) => void;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  parkingSpotId: string;
  maxLots?: number;
}

const BookingDialog = ({ children, onBook, open, onOpenChange, parkingSpotId, maxLots = 1 }: BookingDialogProps) => {
  const [selectedDate, setSelectedDate] = useState<Date>();
  const [selectedTime, setSelectedTime] = useState<string>();
  const [selectedHours, setSelectedHours] = useState<string>("1");
  const [vehicleNumber, setVehicleNumber] = useState<string>("");
  const [currentStep, setCurrentStep] = useState(1);
  const [lotsToBook, setLotsToBook] = useState<number>(1);
  const [availableLots, setAvailableLots] = useState<number>(maxLots);
  const [showAuthDialog, setShowAuthDialog] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);
  const { showFeedback, setShowFeedback, currentBookingId } = useBooking();

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      setIsAuthenticated(!!session);
    };
    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setIsAuthenticated(!!session);
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (selectedDate && selectedTime) {
      fetchAvailableLots();
    }
  }, [selectedDate, selectedTime, parkingSpotId]);

  const fetchAvailableLots = async () => {
    if (!selectedDate || !selectedTime || !parkingSpotId) return;

    // Set the start and end time for the booking
    const [hours, minutes] = selectedTime.split(':').map(Number);
    const bookingStart = new Date(selectedDate);
    bookingStart.setHours(hours, minutes, 0, 0);
    
    const bookingEnd = new Date(bookingStart);
    const hoursToAdd = parseFloat(selectedHours);
    bookingEnd.setHours(bookingEnd.getHours() + Math.floor(hoursToAdd));
    bookingEnd.setMinutes(bookingEnd.getMinutes() + (hoursToAdd % 1) * 60);
    
    try {
      // Get total booked lots for this timeframe
      const { data, error } = await supabase
        .from('bookings')
        .select('lot_count')
        .eq('parking_spot_id', parkingSpotId)
        .eq('status', 'confirmed')
        .or(`start_time.lte.${bookingEnd.toISOString()},end_time.gte.${bookingStart.toISOString()}`);
      
      if (error) throw error;
      
      const totalBookedLots = data?.reduce((sum, booking) => sum + (booking.lot_count || 1), 0) || 0;
      const lotsAvailable = Math.max(0, maxLots - totalBookedLots);
      
      setAvailableLots(lotsAvailable);
      if (lotsToBook > lotsAvailable) {
        setLotsToBook(Math.max(1, lotsAvailable));
      }
    } catch (error) {
      console.error('Error checking available lots:', error);
    }
  };

  const handleBooking = () => {
    // Check authentication before proceeding
    if (!isAuthenticated) {
      setShowAuthDialog(true);
      return;
    }

    if (selectedDate && selectedTime && selectedHours && vehicleNumber) {
      const [hours, minutes] = selectedTime.split(':').map(Number);
      const bookingDate = new Date(selectedDate);
      
      bookingDate.setHours(hours);
      bookingDate.setMinutes(minutes);
      
      const localDate = new Date(
        bookingDate.getFullYear(),
        bookingDate.getMonth(),
        bookingDate.getDate(),
        hours,
        minutes,
        0,
        0
      );

      console.log('Selected date and time:', {
        originalDate: selectedDate,
        selectedTime,
        hours,
        minutes,
        finalDate: localDate,
        finalDateISOString: localDate.toISOString(),
        lotsToBook,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      });
      
      onBook(localDate, selectedTime, selectedHours, vehicleNumber, lotsToBook);
      onOpenChange?.(false);
      
      setSelectedDate(undefined);
      setSelectedTime(undefined);
      setSelectedHours("1");
      setVehicleNumber("");
      setLotsToBook(1);
      setCurrentStep(1);
    }
  };

  const handleAuthSuccess = () => {
    setShowAuthDialog(false);
    // Continue with booking after authentication
    handleBooking();
  };

  const isDateDisabled = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    date.setHours(0, 0, 0, 0);
    return date < today;
  };

  const handleDialogOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      setTimeout(() => {
        setCurrentStep(1);
      }, 300);
    }
    onOpenChange?.(newOpen);
  };

  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const formatTimeFor12HourDisplay = (time24: string | undefined) => {
    if (!time24) return "";
    
    const [hoursStr, minutesStr] = time24.split(':');
    const hours = parseInt(hoursStr, 10);
    const minutes = minutesStr;
    
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 === 0 ? 12 : hours % 12;
    
    return `${displayHours}:${minutes} ${period}`;
  };

  const canProceedToStep2 = !!selectedDate;
  const canProceedToStep3 = !!selectedTime && !!selectedHours && availableLots > 0;
  const canFinish = !!vehicleNumber && lotsToBook > 0 && lotsToBook <= availableLots;

  // Generate lot selection options
  const generateLotOptions = () => {
    const options = [];
    for (let i = 1; i <= availableLots; i++) {
      options.push(
        <SelectItem key={i} value={i.toString()}>
          {i} {i === 1 ? 'lot' : 'lots'}
        </SelectItem>
      );
    }
    return options;
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogTrigger asChild>
          {children}
        </DialogTrigger>
        <DialogContent className="sm:max-w-[425px]" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {currentStep === 1 && <><CalendarCheck className="w-5 h-5 text-blue-500" /> Select Date</>}
              {currentStep === 2 && <><Clock className="w-5 h-5 text-blue-500" /> Select Time & Duration</>}
              {currentStep === 3 && <><Car className="w-5 h-5 text-blue-500" /> Vehicle Details</>}
            </DialogTitle>
          </DialogHeader>
          
          <div className="w-full bg-gray-100 h-2 rounded-full mb-4 overflow-hidden">
            <div 
              className="bg-blue-500 h-full transition-all duration-500 ease-out"
              style={{ width: `${(currentStep / 3) * 100}%` }}
            ></div>
          </div>
          
          <div className="space-y-4 py-4">
            <div className={`transition-all duration-500 ${currentStep === 1 ? 'animate-fade-in' : 'hidden'}`}>
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <CalendarCheck className="w-4 h-4 text-blue-500" />
                  Select Date
                </label>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  className="rounded-md border animate-bounce-in"
                  disabled={isDateDisabled}
                />
              </div>
              
              <Button 
                className="w-full mt-4 animate-pulse-glow" 
                onClick={nextStep}
                disabled={!canProceedToStep2}
              >
                Continue
              </Button>
            </div>
            
            <div className={`space-y-4 transition-all duration-500 ${currentStep === 2 ? 'animate-fade-in' : 'hidden'}`}>
              <TimeSelector 
                selectedTime={selectedTime} 
                onTimeChange={setSelectedTime}
                selectedDate={selectedDate}
                parkingSpotId={parkingSpotId}
              />
              
              <DurationSelector 
                selectedHours={selectedHours} 
                onDurationChange={setSelectedHours} 
              />

              {availableLots === 0 && selectedTime && (
                <div className="p-3 bg-red-50 text-red-700 rounded-md text-sm">
                  No parking lots available for this time. Please select a different time or date.
                </div>
              )}
              
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={prevStep}>
                  Back
                </Button>
                <Button 
                  className="flex-1 animate-pulse-glow" 
                  onClick={nextStep}
                  disabled={!canProceedToStep3}
                >
                  Continue
                </Button>
              </div>
            </div>
            
            <div className={`space-y-4 transition-all duration-500 ${currentStep === 3 ? 'animate-fade-in' : 'hidden'}`}>
              <VehicleNumberInput 
                vehicleNumber={vehicleNumber}
                onVehicleNumberChange={setVehicleNumber}
              />
              
              <div className="space-y-2">
                <label className="text-sm font-medium flex items-center gap-2">
                  <Warehouse className="w-4 h-4 text-blue-500" />
                  How many parking lots do you need?
                </label>
                <Select value={lotsToBook.toString()} onValueChange={(value) => setLotsToBook(parseInt(value))}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select number of lots" />
                  </SelectTrigger>
                  <SelectContent>
                    {generateLotOptions()}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500">
                  {availableLots} {availableLots === 1 ? 'lot' : 'lots'} available for this time slot
                </p>
              </div>
              
              <div className="p-4 bg-blue-50 rounded-md animate-fade-in">
                <h3 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                  <CreditCard className="w-4 h-4" />
                  Booking Summary
                </h3>
                <div className="text-sm space-y-1 text-blue-700">
                  {selectedDate && (
                    <p>Date: {selectedDate.toLocaleDateString()}</p>
                  )}
                  {selectedTime && (
                    <p>Time: {formatTimeFor12HourDisplay(selectedTime)}</p>
                  )}
                  {selectedHours && (
                    <p>Duration: {selectedHours} hour(s)</p>
                  )}
                  <p>Lots: {lotsToBook}</p>
                </div>
              </div>
              
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={prevStep}>
                  Back
                </Button>
                <Button 
                  className="flex-1 bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 animate-pulse-glow" 
                  onClick={handleBooking}
                  disabled={!canFinish}
                >
                  {!isAuthenticated ? 'Sign in to Book' : 'Proceed to Payment'}
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <FeedbackForm
        bookingId={currentBookingId}
        open={showFeedback}
        onOpenChange={setShowFeedback}
      />
      
      <AuthPromptDialog
        open={showAuthDialog}
        onOpenChange={setShowAuthDialog}
        onAuthSuccess={handleAuthSuccess}
        title="Sign in to book parking"
        description="Please sign in to complete your parking reservation."
      />
    </>
  );
};

export default BookingDialog;
